import React, { useState } from 'react';
import { RotateCcw, Search, ShoppingBag, AlertCircle, Clock, X } from 'lucide-react';
import { orderApi } from '../../config/api';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'sonner';

export default function ReorderModal({ isOpen, onClose }) {
  const [phone, setPhone] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(null);
  const { addItem } = useCart();

  if (!isOpen) return null;

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim() || phone.trim().length < 4) {
      toast.error('Bitte gib eine gültige Telefonnummer ein');
      return;
    }
    setLoading(true);
    try {
      const res = await orderApi.getMyOrders(phone.trim());
      setOrders(res.data?.data || []);
    } catch {
      toast.error('Fehler beim Laden der Bestellungen');
    }
    setLoading(false);
  };

  const handleReorder = async (orderNumber) => {
    setReordering(orderNumber);
    try {
      const res = await orderApi.reorder(orderNumber);
      const data = res.data?.data;
      const items = data?.items || [];
      const unavailable = data?.unavailable || [];

      if (items.length === 0) {
        toast.error('Keine verfügbaren Artikel in dieser Bestellung');
        setReordering(null);
        return;
      }

      // Add each item to cart
      items.forEach(item => {
        addItem({
          menu_item_id: item.menu_item_id,
          name: item.name,
          image_url: item.image_url,
          size: item.size || 'default',
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          is_menu_upgrade: item.is_menu_upgrade || false,
          modifiers: item.modifiers || [],
          extras: item.extras || [],
          removed_ingredients: item.removed_ingredients || [],
        });
      });

      const msg = unavailable.length > 0
        ? `${items.length} Artikel hinzugefügt. ${unavailable.length} nicht verfügbar.`
        : `${items.length} Artikel in den Warenkorb gelegt!`;

      toast.success(msg);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nachbestellung fehlgeschlagen');
    }
    setReordering(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="reorder-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#A81C1C]/10 flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-[#A81C1C]" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-[#292524]">Nachbestellen</h2>
              <p className="text-xs text-[#78716C]">Finde deine letzten Bestellungen</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-400 transition-colors" data-testid="reorder-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Phone Search */}
        <form onSubmit={handleSearch} className="p-5 border-b border-neutral-100">
          <label className="block text-sm font-medium text-[#292524] mb-2">Telefonnummer eingeben</label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="0170 1234567"
              className="flex-1 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-[#292524] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#A81C1C]/30 focus:border-[#A81C1C]"
              data-testid="reorder-phone"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-3 bg-[#A81C1C] hover:bg-[#8B1616] disabled:opacity-50 text-white font-medium rounded-xl transition-colors flex items-center gap-2"
              data-testid="reorder-search-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>

        {/* Results */}
        <div className="overflow-y-auto max-h-[50vh] p-5">
          {orders === null ? (
            <div className="text-center py-8 text-neutral-400">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Gib deine Telefonnummer ein, um deine Bestellungen zu finden</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Keine Bestellungen mit dieser Nummer gefunden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div key={order.order_number} className="border border-neutral-200 rounded-xl p-4 hover:border-[#A81C1C]/20 transition-colors" data-testid={`reorder-order-${order.order_number}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-mono font-bold text-[#A81C1C] text-sm">{order.order_number}</span>
                      <div className="flex items-center gap-2 text-xs text-neutral-400 mt-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(order.created_at)}</span>
                        <span>&middot;</span>
                        <span>{order.order_type === 'pickup' ? 'Abholung' : 'Lieferung'}</span>
                      </div>
                    </div>
                    <span className="font-bold text-[#292524]">
                      {'\u20AC'}{(order.pricing?.final_total || order.pricing?.subtotal || 0).toFixed(2).replace('.', ',')}
                    </span>
                  </div>

                  {/* Item preview */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {order.items?.slice(0, 4).map((item, idx) => (
                      <span key={idx} className="text-[10px] bg-neutral-100 text-neutral-600 px-2 py-1 rounded-md">
                        {item.quantity}x {item.name}
                      </span>
                    ))}
                    {order.items?.length > 4 && (
                      <span className="text-[10px] bg-neutral-100 text-neutral-400 px-2 py-1 rounded-md">
                        +{order.items.length - 4} weitere
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => handleReorder(order.order_number)}
                    disabled={reordering === order.order_number}
                    className="w-full py-2.5 bg-[#A81C1C]/10 hover:bg-[#A81C1C]/20 text-[#A81C1C] font-semibold text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid={`reorder-btn-${order.order_number}`}
                  >
                    {reordering === order.order_number ? (
                      <div className="w-4 h-4 border-2 border-[#A81C1C] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    Nochmal bestellen
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
